/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import customTable from '../../../../../share/components/vue/PaginationTable';
import customProgress from "../../../../../share/components/vue/CustomProgress";
import config from '../../../../../share/config';
import { readHistoryData } from './standby';
import { readImageData } from './start';
import { exitLogic } from './terminate';
import { PHASE, STANDBY_TYPE } from './constants';
import { sessionSecurity } from './sessionSecurity';
import { historyInfoTableDataConvert } from './utils';
const moment = require('moment');

export default {
  name: 'requirementSpES006',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      confirmChecked: false,
      agreementChecked: false,
      standbyType: STANDBY_TYPE.CONTROL_HISTORY,
      progress: 0,
      progressMax: 100,
      estimatedTime: 0,
      selectedItem: null,
      selectedStoreItem: null,
      historyData: {},
      displayHistoryInfo: [],
      historyInfoTableColumns: [],
      displayStoreHistoryInfo: [],
      storeHistoryInfoTableColumns: [],
      selectedStoreHistoryInfo: [],
      doneFile: 0,
      fileAmount: 0,
      modal_special_requirement_ES6: false
    };
  },

  components: {
    'custom-Progress': customProgress,
    "custom-table": customTable
  },

  props: {
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.FAILED:
        case PHASE.ERROR:
          return 'danger';
        case PHASE.START:
        case PHASE.ALERT_HISTORY_DATA:
          return 'warning';
        case PHASE.HISTORY_VIEW:
        case PHASE.STORE_HISTORY_VIEW:
        case PHASE.READ_HISTORY_DATA:
        case PHASE.STORE_FILE:
        default:
          return 'secondary';
      }
    },
    // チェックボックスが2つとも押下されない限りtrue(PHASE.START)
    isDisabledAgreementConfirm() {
      if (!this.confirmChecked || !this.agreementChecked) return true;
      return false;
    },
    isDisableButton() {
      if (this.selectedItem === null) return true;
      return false;
    },
    isDisableSelectButton() {
      if (this.selectedStoreItem === null) return true;
      return false;
    },
    isDisableOkButton() {
      if (this.selectedStoreHistoryInfo.length <= 0) return true;
      return false;
    },
    isDisableReleaseButton() {
      if (this.selectedStoreItem === null) return true;
      if (this.selectedStoreItem.preservationTarget === '') return true;
      return false;
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.standbyType = STANDBY_TYPE.CONTROL_HISTORY;
      this.modal_special_requirement_ES6 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.resetSelectItem();
      this.$emit('close-wizard');
      this.modal_special_requirement_ES6 = false;
    },

    /** 警告表示から履歴情報表示へ遷移 */
    async screenMovement() {
      try {
        // スピナー表示
        this.isLoading = true;
        await sessionSecurity(this);
        // 作動履歴 発生日時の履歴読み出しを行う
        if (this.phase === PHASE.START) {
          await readHistoryData(this);
        }
        // standbyTypeが｢HALT・FAIL履歴｣または｢IG ON回数(アイサイトバージョン3の読出し)｣の場合、その履歴読出しを再度行う
        if (this.standbyType === STANDBY_TYPE.HALT_FAIL || this.standbyType === STANDBY_TYPE.IG_ON) {
          await readHistoryData(this);
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 履歴情報テーブルの行選択時のイベント
     * @param {object} rowData 選択された行データ
     */
    historyRowClick(rowData) {
      this.selectedItem = rowData;
      this.displayHistoryInfo.forEach((item) => {
        if (item.displayName === this.selectedItem.displayName) {
          item.fieldSpec = {};
          item.fieldSpec.displayName = config.LIST_ITEM_HIGHLIGHT;
        } else if (item.fieldSpec) {
          delete item.fieldSpec;
        }
      });
    },

    /** 履歴情報表示から保存履歴表示へ遷移 */
    async selectHistory() {
      this.phase = PHASE.STORE_HISTORY_VIEW;
      const storeHistoryArr = this.storeHistoryInfoTableDataConvert();
      this.displayStoreHistoryInfo = [];
      this.$nextTick(() => {
        this.displayStoreHistoryInfo = storeHistoryArr;
      });
    },

    /** 
     * 保存履歴の表示用変換
     * @returns 表示用保存履歴情報
     */
    storeHistoryInfoTableDataConvert() {
      const storeHistoryArr = [];
      this.storeHistoryInfoTableColumns = [
        {
          name: "preservationTarget",
          label: `individual.header_preservation_target`,
        },
        {
          name: "storeItem",
          label: `individual.header_store_item`,
        },
        {
          name: "date",
          label: `individual.header_vehicle_dtc_table_date_time_occurrence`,
        },
        {
          name: "igon",
          label: "message.header_vehicle_dtc_table_igon_counts",
        }
      ];

      this.selectedItem.orderKeys.forEach((orderKey, index) => {
        const targetInfo = this.historyData[this.selectedItem.verKey][this.selectedItem.typeKey][orderKey];
        const storeHistory = {
          id: index,
          preservationTarget: '',
          storeItem: this.selectedItem.displayName,
          date: targetInfo.date ? moment(targetInfo.date, "YYYYMMDDHHmm").format("YYYY/MM/DD HH:mm") : '-',
          igon: targetInfo.igon,
          verKey: this.selectedItem.verKey,
          typeKey: this.selectedItem.typeKey,
          orderKey: orderKey
        };
        storeHistoryArr.push(storeHistory);
      });
      return storeHistoryArr;
    },

    /**
     * 履歴情報テーブルの行選択時のイベント
     * @param {object} rowData 選択された行データ
     */
    historyStoreRowClick(rowData) {
      this.selectedStoreItem = rowData;
      this.displayStoreHistoryInfo.forEach((item) => {
        if (item.orderKey === this.selectedStoreItem.orderKey) {
          item.fieldSpec = {};
          item.fieldSpec.preservationTarget = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.storeItem = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.date = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.igon = config.LIST_ITEM_HIGHLIGHT;
        } else if (item.fieldSpec) {
          delete item.fieldSpec;
        }
      });
    },

    /** テーブルの保存対象の印を解除する */
    releaseRow() {
      this.displayStoreHistoryInfo.forEach(item => {
        if (this.selectedStoreItem.orderKey === item.orderKey) {
          item.preservationTarget = '';
        }
      });

      const index = this.selectedStoreHistoryInfo.findIndex(item => item.orderKey === this.selectedStoreItem.orderKey);
      this.selectedStoreHistoryInfo.splice(index, 1);
      this.selectedStoreItem.preservationTarget = '';
    },

    /** 選択した行を選択状態にする */
    selectRow() {
      const match = this.selectedStoreHistoryInfo.some(item => item.orderKey === this.selectedStoreItem.orderKey);
      if (!match) this.selectedStoreHistoryInfo.push(this.selectedStoreItem);
      this.checkStoreTarget();
      this.selectedStoreItem.preservationTarget = '*';
    },

    /** すべての行を選択状態にする */
    selectAllRow() {
      this.selectedStoreHistoryInfo = [];
      this.selectedStoreHistoryInfo.push(...this.displayStoreHistoryInfo);
      this.checkStoreTarget();
      if (this.selectedStoreItem) {
        this.selectedStoreItem.preservationTarget = '*';
      }
    },

    /** 保存対象の行に*を付ける */
    checkStoreTarget() {
      this.displayStoreHistoryInfo.forEach(item => {
        this.selectedStoreHistoryInfo.forEach(i => {
          if (item.orderKey === i.orderKey) {
            item.preservationTarget = '*';
          }
        });
      });
    },

    /** 保存履歴表示から履歴データ読み出し警告へ遷移 */
    async saveHistory() {
      // バージョン4の場合の1レコード辺りの所要時間は11分(660秒)、バージョン3の場合は2分40秒(160秒)
      // standTypeがIG ON以外の場合はバージョン4、IG ONの場合はバージョン3という判定
      const variable = this.standbyType !== STANDBY_TYPE.IG_ON ? 660 : 160;
      this.estimatedTime = Math.ceil(this.selectedStoreHistoryInfo.length * variable / 60);
      // ファイルの総量は選択したレコードのlength
      this.fileAmount = this.selectedStoreHistoryInfo.length;
      this.phase = PHASE.ALERT_HISTORY_DATA;
    },

    /** 履歴データ読み出しを実行する */
    async execute() {
      try {
        // データ読出し前に選択したデータをid順にソートする
        this.selectedStoreHistoryInfo.sort((a, b) => {
          if (a.id > b.id) {
            return 1;
          }
          return -1;
        });
        this.doneFile = 1;
        this.isLoading = true;
        this.phase = PHASE.READ_HISTORY_DATA;
        // データ読み出し用変数
        let nextReadDid = '';
        let downloadUuid = '';
        let fileUrl = '';
        let isLastRecord = this.selectedStoreHistoryInfo.length === 1;
        let savedTotalFileSize = 0;

        do {
          // レコードの先頭のデータを取得する
          const targetRecord = this.selectedStoreHistoryInfo[0];
          const response = await readImageData(targetRecord, nextReadDid, downloadUuid, isLastRecord, savedTotalFileSize, this);
          // レスポンスが存在しない場合、またはPhaseが"PHASE.READ_HISTORY_DATA"以外の場合、エラーに倒れたので処理を中断
          if (!response || this.phase !== PHASE.READ_HISTORY_DATA) {
            return;
          }
          const readResult = response.data.user_data;
          this.progress = readResult.progress;
          fileUrl = readResult.fileUrl;
          // nextReadDidが存在しない場合、1履歴分のデータ読み出しが完了と判断
          if (!readResult.nextReadDid) {
            // fileURLが存在する場合、ファイルをDLする
            if (fileUrl) {
              await this.readResultOutputFile(fileUrl, readResult.fileExtension);
            }
            // 取得した先頭のレコードを削除する
            this.selectedStoreHistoryInfo.shift();
            // まだ読み出すデータが残っている場合、完了ファイル数を+1し、
            // 次に取得するDIDとプログレス値を初期化する
            if (this.selectedStoreHistoryInfo.length > 0) {
              this.doneFile++;
              nextReadDid = '';
              this.progress = 0;
              // 1履歴分のデータの合計サイズを更新しておく
              savedTotalFileSize = readResult.totalFileSize;
              // 履歴のlengthが残り1の場合、最後のレコードと判断する
              isLastRecord = this.selectedStoreHistoryInfo.length === 1;
            }
          } else {
            // レスポンスで返却された次に取得するDIDとダウンロード用uuidを設定する
            nextReadDid = readResult.nextReadDid;
            downloadUuid = readResult.downloadUuid;
          }
          // レコードのlengthが0以下になるまでループする
        } while (this.selectedStoreHistoryInfo.length > 0);
        // 全てのデータ読み出しが完了し、現在のphaseがエラーでなければ成功画面に遷移する
        if (this.phase !== PHASE.ERROR) {
          this.phase = PHASE.SUCCESS;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * ファイル出力
     * @param {string} fileUrl preSignedURL
     * @param {string} fileExtension ファイル拡張子
     */
    async readResultOutputFile(fileUrl, fileExtension) {
      try {
        // ファイル名を変更
        const formattedDate = moment().format("YYYYMMDDHHmmss");

        //URLからファイルをダウンロード
        await fetch(fileUrl)
          .then((response) => response.blob())
          .then((myBlob) => {
            const objUrl = URL.createObjectURL(myBlob);
            const link = document.createElement("a");
            link.href = objUrl;
            link.download = formattedDate + "." + fileExtension;
            link.click();
            link.remove();
            URL.revokeObjectURL(objUrl);
          })
          .catch(() => {
            this.phase = PHASE.ERROR;
          });
      } catch {
        this.phase = PHASE.ERROR;
      }
    },

    /**
     * 任意の画面に戻る
     * @param {string} dst フェーズ情報
     */
    backTo(dst) {
      // 画面遷移
      this.phase = dst;
      // 画面遷移度に、画面に合わせた表示の初期化を行う
      switch (dst) {
        case PHASE.HISTORY_VIEW:
          this.$nextTick(() => {
            this.resetSelectItem(PHASE.HISTORY_VIEW);
          });
          break;
        case PHASE.STORE_HISTORY_VIEW:
          this.$nextTick(() => {
            this.resetSelectItem(PHASE.STORE_HISTORY_VIEW);
          });
          break;
        default:
          break;
      }
    },

    /**
     * 表示している画面の選択された行データのリセット
     */
    async resetSelectItem() {
      switch (this.phase) {
        case PHASE.HISTORY_VIEW:
          this.displayHistoryInfo.forEach(item => {
            if (item.fieldSpec) {
              delete item.fieldSpec;
            }
          });
          this.displayHistoryInfo = historyInfoTableDataConvert(this);
          this.selectedItem = null;
          // 選択済みの保存履歴配列も初期化しておく
          this.selectedStoreHistoryInfo = [];
          break;
        case PHASE.STORE_HISTORY_VIEW:
          this.displayStoreHistoryInfo.forEach(item => {
            item.preservationTarget = '';
            if (item.fieldSpec) {
              delete item.fieldSpec;
            }
          });
          this.displayStoreHistoryInfo = this.storeHistoryInfoTableDataConvert();
          this.selectedStoreHistoryInfo = [];
          break;
        default:
          break;
      }
    },

    /** 終了処理（要求） */
    async exitLogic() {
      await exitLogic(this);
    },
  },

  async mounted() {
    this.init();
  },
};